import type { CharacteristicValueType } from '@/types'
import { sanitizeBarcode } from '@/utils/nomenclature/barcode'
import {
  isSlottedCharacteristicType,
  isUniformSlottedEligible,
} from '@/utils/search/characteristics/slottedCharacteristics'

import type {
  ActiveCharacteristicFilter,
  BackendSort,
  EmptyCharacteristicsFilter,
  NomenclatureAdminSearchRequest,
  NomenclatureAdminSearchResponse,
  NomenclatureRow,
} from '../AttributeProcessing.types'

export const DEFAULT_EMPTY_CHARACTERISTICS_FILTER: EmptyCharacteristicsFilter =
  { hasEmptyCharacteristics: null, codes: [], slots: {} }

export interface AdminSearchParams {
  page: number
  pageSize: number
  query: string
  barcode: string
  brandId: string
  categoryId: string
  parentId: string
  countryIds: string[]
  unitIds: string[]
  isDiscontinued: boolean | null
  isFilled: boolean | null
  multiplicity: boolean | null
  sort: BackendSort
  characteristicFilters: ActiveCharacteristicFilter[]
  emptyFilter: EmptyCharacteristicsFilter
  characteristicTypes: Map<number, CharacteristicValueType>
}

export const hasEmptyCharacteristicsFilter = ({
  hasEmptyCharacteristics,
  codes,
}: EmptyCharacteristicsFilter) =>
  hasEmptyCharacteristics !== null || codes.length > 0

const buildCharacteristicFilters = (filters: ActiveCharacteristicFilter[]) => {
  const filter: Record<string, string[]> = {}
  const ranges: NonNullable<NomenclatureAdminSearchRequest['ranges']> = {}
  let matchUniform = false

  filters.forEach((item) => {
    if (item.type === 'NUMERIC') {
      const range = {
        ...(item.rangeMin !== '' ? { min: Number(item.rangeMin) } : {}),
        ...(item.rangeMax !== '' ? { max: Number(item.rangeMax) } : {}),
      }
      if (Object.keys(range).length > 0) ranges[String(item.code)] = range
    } else if (item.chosenOptions.length > 0) {
      filter[String(item.code)] = item.chosenOptions
    }

    if (
      isSlottedCharacteristicType(item.type) &&
      item.matchUniform &&
      isUniformSlottedEligible(item.chosenOptions)
    ) {
      matchUniform = true
    }
  })

  return { filter, ranges, matchUniform }
}

const buildEmptyFilters = (
  { hasEmptyCharacteristics, codes, slots }: EmptyCharacteristicsFilter,
  characteristicTypes: Map<number, CharacteristicValueType>
): Pick<
  NomenclatureAdminSearchRequest,
  'hasEmptyCharacteristics' | 'emptyCharacteristicCodes' | 'emptySlots'
> => {
  const getSlots = (code: number) => {
    const type = characteristicTypes.get(code)
    const codeSlots = slots[String(code)] ?? []
    return type !== undefined &&
      isSlottedCharacteristicType(type) &&
      codeSlots.length > 0
      ? codeSlots
      : null
  }
  const emptySlots = Object.fromEntries(
    codes.flatMap((code) => {
      const codeSlots = getSlots(code)
      return codeSlots ? [[String(code), codeSlots]] : []
    })
  )
  const emptyCharacteristicCodes = codes.filter((code) => !getSlots(code))

  return {
    ...(hasEmptyCharacteristics !== null ? { hasEmptyCharacteristics } : {}),
    ...(emptyCharacteristicCodes.length > 0
      ? { emptyCharacteristicCodes }
      : {}),
    ...(Object.keys(emptySlots).length > 0 ? { emptySlots } : {}),
  }
}

export const buildAdminSearchRequest = (
  params: AdminSearchParams
): NomenclatureAdminSearchRequest => {
  const { filter, ranges, matchUniform } = buildCharacteristicFilters(
    params.characteristicFilters
  )
  const query = params.query.trim()
  const barcode = sanitizeBarcode(params.barcode)
  const { sort } = params

  return {
    page: params.page,
    pageSize: params.pageSize,
    ...(query ? { query } : {}),
    ...(barcode ? { barcode } : {}),
    ...(params.brandId ? { brandIds: [params.brandId] } : {}),
    ...(params.categoryId ? { categoryIds: [params.categoryId] } : {}),
    ...(params.parentId ? { parentIds: [params.parentId] } : {}),
    ...(params.countryIds.length > 0
      ? { countryOfOriginIds: params.countryIds }
      : {}),
    ...(params.unitIds.length > 0
      ? { unitOfMeasurementIds: params.unitIds }
      : {}),
    ...(params.isDiscontinued !== null
      ? { isDiscontinued: params.isDiscontinued }
      : {}),
    ...(params.isFilled !== null ? { isFilled: params.isFilled } : {}),
    ...(params.multiplicity !== null
      ? { multiplicity: params.multiplicity }
      : {}),
    ...(sort.article ? { sortByArticle: sort.article.toUpperCase() } : {}),
    ...(sort.description
      ? { sortByDescription: sort.description.toUpperCase() }
      : {}),
    ...(Object.keys(filter).length > 0 ? { filter } : {}),
    ...(Object.keys(ranges).length > 0 ? { ranges } : {}),
    ...(matchUniform ? { matchUniformSlottedCharacteristics: true } : {}),
    ...buildEmptyFilters(params.emptyFilter, params.characteristicTypes),
  }
}

export const toNomenclatureRows = (
  response: NomenclatureAdminSearchResponse
): NomenclatureRow[] =>
  response.nomenclatures.content.map((item) => ({
    id: item.id,
    code: item.code,
    description: item.description,
    article: item.article,
    brandName: item.brandDto?.description ?? '',
    categoryName: item.categoryDto?.description ?? '',
    unitName: item.unitOfMeasurementDto?.description ?? '',
    countryName: item.countryDto?.description ?? '',
    parentName: item.parentModel?.description ?? '',
    dynamicValue: '',
    flashStatus: null,
    errorMessage: null,
  }))
